import { useEffect } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'

import Sidebar from '../components/sidebar'
import Notification from '../components/notification'
import Header from '../components/header'
import { SearchBar } from '../components/searchbar'

import Downloaded from './downloaded'

import Movies from './movies'
import Movie from './movie'
import TvShows from './tvshows'
import TvShow from './tvshow'
import { Season } from './season'
import { Search } from './search'
import { Settings } from './settings'
import Support from './support'
import Stats from './stats'
import { Admin } from './admin'

import MyHistory from './myhistory'

import HlsPlayer from './hlsplayer'

import Spinner from '../components/spinner'

import { useCurrentPath } from '../state/ui'
import { connectWs } from '../state/ws'
import { useSettings, useUser } from '../queries/user'
import './app.css'
import { MusicSearch } from './music/search'
import { MusicRelease } from './music/release'
import { Playlist } from './music/playlist'
import { AudioPlayerProvider } from '../components/audioPlayer/provider'
import { Music } from './music'
import { MusicArtist } from './music/artist'
import { MusicPrepare } from './music/prepare'
import { Books } from './books'
import { MyLibrary } from './myLibrary'

export function App() {
  const currentPath = useCurrentPath()
  const user = useUser()
  // Kicked off here so it is warm by the time a page reads it, but not awaited.
  useSettings()

  const hasToken = Boolean(window.localStorage.getItem('token'))

  useEffect(() => {
    if (hasToken) connectWs()
  }, [hasToken])

  if (!hasToken) {
    return <Navigate to="/" replace />
  }

  /*
   * Only the user blocks the first paint -- the sidebar renders their name and
   * has nothing to show without it. Settings deliberately do not: they cost two
   * sequential requests (/settings then /providers) and nothing in the shell
   * reads them, so gating on them would hold the whole app behind a round-trip
   * it does not need. The pages that do read them wait for them through
   * loadSettings().
   */
  if (user.isPending) {
    return (
      <div style={{ width: '100%', margin: 'auto' }}>
        <Spinner />
        <p style={{ textAlign: 'center' }}>Loading settings...</p>
      </div>
    )
  }

  /*
   * A failed lookup is not fatal to the session. An invalid token already
   * redirects to the login page from the query itself; anything else (a blip
   * on social or search) leaves the shell usable rather than replacing the
   * whole app with an error string, which is how this behaved before.
   */
  if (user.error) {
    console.error('failed to load the current user', user.error)
  }

  return (
    <div className="router">
      <AudioPlayerProvider>
        <Sidebar />
        <div className="container">
          <Notification />
          <Header
            title={
              currentPath.length > 0 ? currentPath.join(' / ') : 'Enjoy beautiful contents'
            }
          />
          <div className="page">
            <SearchBar />
            <Routes>
            <Route path="downloaded" element={<Downloaded />} />
            <Route path="movies" element={<Movies />} />
            <Route path="movies/:id" element={<Movie />} />
            <Route path="tvshows" element={<TvShows />} />
            <Route path="tvshows/:id" element={<TvShow />} />
            <Route path="tvshows/:id/:season" element={<Season />} />
            <Route path="tvshows/:id/:season/:episode" element={<Movie />} />
            <Route path="search" element={<Search />} />
            <Route path="music" element={<Music />} />
            <Route path="music/search" element={<MusicSearch />} />
            <Route path="music/prepare" element={<MusicPrepare />} />
            <Route path="music/playlist" element={<Playlist />} />
            <Route path="music/:artist" element={<MusicArtist />} />
            <Route path="music/:artist/:release" element={<MusicRelease />} />
            <Route path="books" element={<Books />} />
            <Route path="books/my-library" element={<MyLibrary />} />
            <Route path="settings" element={<Settings />} />
            <Route path="support" element={<Support />} />
            <Route path="stats" element={<Stats />} />
            <Route path="admin" element={<Admin />} />

            <Route path="myhistory" element={<MyHistory />} />
            <Route path="hls/:file" element={<HlsPlayer />} />
            </Routes>
          </div>
        </div>
      </AudioPlayerProvider>
    </div>
  )
}
